import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './Lieferavis.css'

// Lieferavis — the day's expected deliveries, checked by reference number at the
// kiosk. Plans/Lieferavis_Plan.md. Admins only; the rules (matching, what an import
// replaces, auto-delete) live in server/services/deliveryService.js.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'
// SSE makes an arrival appear immediately; this is only the floor if an event is missed.
const REFRESH_MS = 60000

// Column names the import recognises, lower case. Exports of different systems call
// the same thing differently; the template uses the first of each.
const HEADER_ALIASES = {
  reference: ['referenznummer', 'referenz', 'reference', 'ref', 'auftragsnummer', 'auftrag', 'lieferscheinnummer', 'lieferschein', 'bestellnummer', 'avisnummer'],
  company:   ['firma', 'company', 'spedition', 'lieferant', 'unternehmen'],
  name:      ['name', 'fahrer', 'ansprechpartner', 'kontakt'],
  date:      ['datum', 'lieferdatum', 'date', 'liefertag', 'anliefertag'],
  notes:     ['notiz', 'notizen', 'bemerkung', 'hinweis', 'notes'],
}

const shiftDay = (iso, days) => {
  // Noon, so a DST switch cannot roll the date back a day.
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const fmtDay = (iso) => (iso ? iso.split('-').reverse().join('.') : '—')
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '')

const fieldLabel = (field) => {
  const raw = field.label
  if (!raw) return field.id
  if (typeof raw === 'string') return raw
  return raw.de || raw.en || Object.values(raw)[0] || field.id
}

const flowLabel = (flowType) => {
  if (flowType === 'terminal') return 'Kiosk'
  if (flowType.startsWith('terminal_')) {
    return `Kiosk – ${flowType.slice('terminal_'.length).split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')}`
  }
  return flowType
}

/** One CSV line → cells. Handles "quoted; values" and "" escapes, as the export writes them. */
const splitCsvLine = (line, sep) => {
  const out = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++ }
      else if (ch === '"') quoted = false
      else cur += ch
    } else if (ch === '"') quoted = true
    else if (ch === sep) { out.push(cur.trim()); cur = '' }
    else cur += ch
  }
  out.push(cur.trim())
  return out
}

/**
 * CSV text → { rows, error }. Rows carry the 1-based data line so the server's
 * rejection reasons can point at it. Only parsing happens here; dates and duplicates
 * are judged by the server, which knows the tenant's "today".
 */
const parseDeliveryCsv = (text) => {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim())
  if (lines.length < 2) return { error: 'Datei zu kurz – die erste Zeile muss die Spaltenköpfe enthalten.' }

  const head = lines[0]
  const sep = [';', '\t', ','].reduce((best, s) => (head.split(s).length > head.split(best).length ? s : best), ';')
  const headers = splitCsvLine(head, sep).map(h => h.toLowerCase())
  const index = Object.fromEntries(Object.entries(HEADER_ALIASES).map(([key, names]) => [key, headers.findIndex(h => names.includes(h))]))

  if (index.reference < 0 || index.date < 0) {
    return { error: 'Spalten „Referenznummer" und „Datum" nicht gefunden. Bitte die Vorlage verwenden.' }
  }

  const rows = lines.slice(1).map((l, i) => {
    const cells = splitCsvLine(l, sep)
    const cell = (key) => (index[key] >= 0 ? cells[index[key]] ?? '' : '')
    return { line: i + 1, reference: cell('reference'), company: cell('company'), name: cell('name'), date: cell('date'), notes: cell('notes') }
  })
  return { rows }
}

const readFileText = (file) =>
  file.text().then(text => {
    // Excel's "CSV (Trennzeichen-getrennt)" is Windows-1252. Its umlauts are not valid
    // UTF-8, so file.text() replaces each with U+FFFD ("M�ller") — re-read in that
    // encoding when one shows up. A real UTF-8 file never contains U+FFFD.
    if (!text.includes('�')) return text
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = e => resolve(e.target.result)
      reader.onerror = reject
      reader.readAsText(file, 'windows-1252')
    })
  })

const downloadCsv = (filename, header, rows) => {
  const esc = (v) => {
    const s = String(v ?? '')
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = '﻿' + [header, ...rows].map(r => r.map(esc).join(';')).join('\n') + '\n'
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const emptyEntry = (date = '') => ({ reference: '', company: '', name: '', notes: '', date })

const Lieferavis = () => {
  const { apiFetch, isOwner, admin, getToken, account } = useAuth()

  const [tenants, setTenants] = useState([])
  const [tenantId, setTenantId] = useState('')
  const [today, setToday] = useState(null)
  const [day, setDay] = useState(null)              // the day shown; null until the server says what today is
  const [deliveries, setDeliveries] = useState([])
  const [settings, setSettings] = useState(null)
  const [savedSettings, setSavedSettings] = useState(null)
  const [checkedFlows, setCheckedFlows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [search, setSearch] = useState('')
  const [newEntry, setNewEntry] = useState(emptyEntry())
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState(emptyEntry())
  const [dragOver, setDragOver] = useState(false)
  const [importing, setImporting] = useState(false)
  const [pendingImport, setPendingImport] = useState(null) // { fileName, rows, keepManual, preview }
  const [settingsSaving, setSettingsSaving] = useState(false)
  const fileRef = useRef()

  const showSuccess = (msg) => {
    setSuccess(msg)
    setTimeout(() => setSuccess(null), 3500)
  }

  // ── Tenant ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOwner) {
      // Only tenants that have Lieferavis switched on in Funktionen — for the others
      // the list would be editable here but checked nowhere.
      apiFetch('/api/admin/tenants').then(r => r.json()).then(data => {
        const list = (Array.isArray(data) ? data : []).filter(t => t.features?.deliveries_enabled === true)
        setTenants(list)
        if (list.length > 0) setTenantId(list[0].id)
        else setLoading(false)
      }).catch(() => setLoading(false))
    } else if (admin?.tenant_id) {
      setTenantId(admin.tenant_id)
    }
  }, [isOwner, admin?.tenant_id])

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!tenantId) return
    const query = day ? `?from=${day}&to=${day}` : ''
    try {
      const res = await apiFetch(`/api/admin/deliveries/${tenantId}${query}`)
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Laden fehlgeschlagen')
      setToday(data.today)
      // First load has no day yet (only the server knows the tenant's "today"): it
      // returns today + tomorrow, of which today is shown.
      if (!day) setDay(data.today)
      setDeliveries((data.deliveries || []).filter(d => day || d.delivery_date === data.today))
      setSettings(s => s ?? data.settings)
      setSavedSettings(data.settings)
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [tenantId, day, apiFetch])

  useEffect(() => {
    setSettings(null)
    setDay(null)
    setLoading(true)
  }, [tenantId])

  useEffect(() => {
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => clearInterval(timer)
  }, [load])

  useEffect(() => {
    if (day) setNewEntry(e => ({ ...e, date: day }))
  }, [day])

  // Which flows check against this list — so an admin can see it is actually in use.
  useEffect(() => {
    if (!tenantId) return
    apiFetch(`/api/admin/data-entry-steps/${tenantId}`)
      .then(r => r.json())
      .then(steps => {
        if (!Array.isArray(steps)) return
        setCheckedFlows(steps
          .filter(s => s.config?.deliveryCheck)
          .map(s => ({ flowType: s.flowType, field: (s.config.fields || []).find(f => f.isReference) })))
      })
      .catch(() => {})
  }, [tenantId, apiFetch])

  // Live: an arrival at the kiosk, or another admin's import/edit.
  const esRef = useRef(null)
  useEffect(() => {
    if (!account || !tenantId) return
    let active = true
    let retry = null

    async function connect() {
      if (!active) return
      let token
      try { token = await getToken() } catch { retry = setTimeout(connect, 5000); return }
      if (!active) return
      const es = new EventSource(`${BACKEND_URL}/api/admin/events?token=${token}`)
      esRef.current = es
      const refresh = (e) => {
        try { if (JSON.parse(e.data).tenantId !== tenantId) return } catch { /* reload anyway */ }
        load()
      }
      es.addEventListener('delivery-arrived', refresh)
      es.addEventListener('deliveries-changed', refresh)
      es.onerror = () => { es.close(); if (active) retry = setTimeout(connect, 5000) }
    }
    connect()

    return () => {
      active = false
      if (retry) clearTimeout(retry)
      esRef.current?.close()
    }
  }, [account, tenantId, getToken, load])

  // ── Single rows ───────────────────────────────────────────────────────────
  const errorFrom = async (res, fallback) => (await res.json().catch(() => ({}))).error || fallback

  const addEntry = async () => {
    if (!newEntry.reference.trim() || !newEntry.date) return
    setError(null)
    const res = await apiFetch(`/api/admin/deliveries/${tenantId}`, { method: 'POST', body: JSON.stringify(newEntry) })
    if (!res.ok) return setError(await errorFrom(res, 'Hinzufügen fehlgeschlagen'))
    setNewEntry(emptyEntry(day))
    showSuccess('Eintrag hinzugefügt')
    load()
  }

  const saveEdit = async (id) => {
    setError(null)
    const res = await apiFetch(`/api/admin/deliveries/${tenantId}/${id}`, { method: 'PATCH', body: JSON.stringify(editForm) })
    if (!res.ok) return setError(await errorFrom(res, 'Speichern fehlgeschlagen'))
    setEditingId(null)
    load()
  }

  const setArrived = async (id, arrived) => {
    if (!arrived && !window.confirm('Ankunft zurücksetzen? Der Eintrag gilt dann wieder als offen.')) return
    setError(null)
    const res = await apiFetch(`/api/admin/deliveries/${tenantId}/${id}`, { method: 'PATCH', body: JSON.stringify({ arrived }) })
    if (!res.ok) return setError(await errorFrom(res, 'Speichern fehlgeschlagen'))
    load()
  }

  const deleteEntry = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return
    const res = await apiFetch(`/api/admin/deliveries/${tenantId}/${id}`, { method: 'DELETE' })
    if (!res.ok) return setError(await errorFrom(res, 'Löschen fehlgeschlagen'))
    load()
  }

  // ── Import: parse → preview dialog → apply ────────────────────────────────
  const requestPreview = async (rows, keepManual) => {
    const res = await apiFetch(`/api/admin/deliveries/${tenantId}/import/preview`, {
      method: 'POST', body: JSON.stringify({ rows, keepManual }),
    })
    if (!res.ok) throw new Error(await errorFrom(res, 'Vorschau fehlgeschlagen'))
    return res.json()
  }

  const handleFile = async (file) => {
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    setError(null)
    setImporting(true)
    try {
      const { rows, error: parseError } = parseDeliveryCsv(await readFileText(file))
      if (parseError) throw new Error(parseError)
      const preview = await requestPreview(rows, true)
      setPendingImport({ fileName: file.name, rows, keepManual: true, preview })
    } catch (err) {
      setError(err.message)
    } finally {
      setImporting(false)
    }
  }

  const toggleKeepManual = async (keepManual) => {
    setPendingImport(p => ({ ...p, keepManual }))
    try {
      const preview = await requestPreview(pendingImport.rows, keepManual)
      setPendingImport(p => (p ? { ...p, keepManual, preview } : p))
    } catch (err) {
      setError(err.message)
    }
  }

  const applyImport = async () => {
    setImporting(true)
    setError(null)
    try {
      const res = await apiFetch(`/api/admin/deliveries/${tenantId}/import`, {
        method: 'POST', body: JSON.stringify({ rows: pendingImport.rows, keepManual: pendingImport.keepManual }),
      })
      if (!res.ok) throw new Error(await errorFrom(res, 'Import fehlgeschlagen'))
      const result = await res.json()
      setPendingImport(null)
      showSuccess(`${result.validCount} Einträge importiert${result.rejected.length ? `, ${result.rejected.length} übersprungen` : ''}`)
      load()
    } catch (err) {
      setError(err.message)
    } finally {
      setImporting(false)
    }
  }

  const downloadTemplate = () => downloadCsv('lieferavis_vorlage.csv',
    ['Referenznummer', 'Firma', 'Name', 'Datum', 'Notiz'],
    [['4711-0815', 'Spedition Beispiel GmbH', 'Max Mustermann', fmtDay(today), '']])

  const exportDay = () => downloadCsv(`lieferavis_${day}.csv`,
    ['Referenznummer', 'Firma', 'Name', 'Datum', 'Notiz', 'Status', 'Angekommen um'],
    deliveries.map(d => [d.reference, d.company, d.name, fmtDay(d.delivery_date), d.notes,
      d.matched_at ? 'Angekommen' : 'Offen', fmtTime(d.matched_at)]))

  // ── Settings ──────────────────────────────────────────────────────────────
  const settingsDirty = settings && savedSettings && JSON.stringify(settings) !== JSON.stringify(savedSettings)

  const saveSettings = async () => {
    setSettingsSaving(true)
    setError(null)
    const res = await apiFetch(`/api/admin/deliveries-settings/${tenantId}`, { method: 'PUT', body: JSON.stringify(settings) })
    if (res.ok) {
      const saved = await res.json()
      setSettings(saved)
      setSavedSettings(saved)
      showSuccess('Einstellungen gespeichert')
    } else {
      setError(await errorFrom(res, 'Einstellungen konnten nicht gespeichert werden'))
    }
    setSettingsSaving(false)
  }

  const setRetention = (patch) => setSettings(s => ({ ...s, retention: { ...s.retention, ...patch } }))

  // ── Derived ───────────────────────────────────────────────────────────────
  const arrived = deliveries.filter(d => d.matched_at).length
  const q = search.trim().toLowerCase()
  const visible = deliveries.filter(d => !q ||
    [d.reference, d.company, d.name, d.notes].some(v => (v || '').toLowerCase().includes(q)))

  if (loading) return <div className="loading-state"><span className="spinner" /></div>

  if (isOwner && !tenantId) {
    return (
      <div>
        <div className="page-header">
          <div><h1>Lieferavis</h1></div>
        </div>
        <div className="card dv-empty">
          Lieferavis ist bei keinem Mandanten eingeschaltet. Unter „Funktionen" für einen Mandanten aktivieren.
        </div>
      </div>
    )
  }

  const dayLabel = day === today ? 'Heute' : day === (today && shiftDay(today, 1)) ? 'Morgen' : fmtDay(day)

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Lieferavis</h1>
          <p>Angekündigte Lieferungen. Am Terminal wird die eingegebene Referenznummer mit dieser Liste abgeglichen.</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      {isOwner && tenants.length > 0 && (
        <div className="card dv-tenant-card">
          <div className="form-group">
            <label>Mandant</label>
            <select className="form-control" value={tenantId} onChange={e => setTenantId(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* ── Day + counter ─────────────────────────────────────────────── */}
      <div className="card">
        <div className="dv-day-bar">
          <div className="dv-day-tabs">
            <button className={`btn btn-sm ${day === today ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setDay(today)}>Heute</button>
            <button className={`btn btn-sm ${day === shiftDay(today, 1) ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setDay(shiftDay(today, 1))}>Morgen</button>
            <input type="date" className="form-control dv-day-picker" value={day || ''}
              onChange={e => e.target.value && setDay(e.target.value)} />
          </div>
          <div className="dv-counter">
            <span className="dv-counter-value">{arrived} von {deliveries.length}</span>
            <span className="dv-counter-label">angekommen · {dayLabel}</span>
          </div>
        </div>
        {deliveries.length > 0 && (
          <div className="dv-progress"><div className="dv-progress-bar" style={{ width: `${(arrived / deliveries.length) * 100}%` }} /></div>
        )}
        {checkedFlows.length === 0 ? (
          <p className="dv-flow-hint dv-flow-hint--warn">
            Noch in keinem Flow aktiv. Im Flow Builder beim Dateneingabe-Schritt ein Feld als „Referenz" markieren
            und „Referenznummer mit Lieferavis abgleichen" einschalten.
          </p>
        ) : (
          <p className="dv-flow-hint">
            Geprüft in: {checkedFlows.map(f => `${flowLabel(f.flowType)}${f.field ? ` (${fieldLabel(f.field)})` : ''}`).join(', ')}
          </p>
        )}
      </div>

      {/* ── Import ────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="dv-section-header">
          <div className="section-title">CSV-Import</div>
          <button className="btn btn-ghost btn-sm" onClick={downloadTemplate}>Vorlage herunterladen</button>
        </div>
        <p className="dv-hint">
          Ersetzt für jeden Tag in der Datei die komplette Liste dieses Tages. Andere Tage bleiben unverändert,
          bereits angekommene Einträge werden nie entfernt. Vor dem Übernehmen wird eine Vorschau angezeigt.
        </p>
        <div
          className={`dv-drop-zone${dragOver ? ' dv-drop-zone--active' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]) }}
          onClick={() => fileRef.current?.click()}
        >
          <span className="dv-drop-icon">📂</span>
          {importing && !pendingImport
            ? <><span className="spinner" /><span>Datei wird geprüft…</span></>
            : <span>CSV-Datei hierher ziehen oder klicken zum Auswählen</span>}
          <input ref={fileRef} type="file" accept=".csv,.txt" hidden onChange={e => handleFile(e.target.files[0])} />
        </div>
      </div>

      {/* ── List ──────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="dv-section-header">
          <div className="section-title">Lieferungen · {fmtDay(day)}</div>
          <div className="dv-list-actions">
            {deliveries.length > 5 && (
              <input className="form-control dv-search" placeholder="Suchen…" value={search} onChange={e => setSearch(e.target.value)} />
            )}
            {deliveries.length > 0 && <button className="btn btn-ghost btn-sm" onClick={exportDay}>Exportieren</button>}
          </div>
        </div>

        <div className="dv-add-grid">
          <input className="form-control" placeholder="Referenznummer *" value={newEntry.reference}
            onChange={e => setNewEntry(f => ({ ...f, reference: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addEntry()} />
          <input className="form-control" placeholder="Firma" value={newEntry.company}
            onChange={e => setNewEntry(f => ({ ...f, company: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addEntry()} />
          <input className="form-control" placeholder="Name" value={newEntry.name}
            onChange={e => setNewEntry(f => ({ ...f, name: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addEntry()} />
          <input className="form-control" type="date" value={newEntry.date}
            onChange={e => setNewEntry(f => ({ ...f, date: e.target.value }))} />
          <input className="form-control" placeholder="Notiz" value={newEntry.notes}
            onChange={e => setNewEntry(f => ({ ...f, notes: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addEntry()} />
          <button className="btn btn-primary dv-add-btn" disabled={!newEntry.reference.trim() || !newEntry.date} onClick={addEntry}>
            Hinzufügen
          </button>
        </div>

        {visible.length === 0 ? (
          <div className="dv-empty">{deliveries.length === 0 ? 'Für diesen Tag sind keine Lieferungen eingetragen.' : 'Keine Treffer für diese Suche.'}</div>
        ) : (
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Referenz</th>
                  <th>Firma</th>
                  <th>Name</th>
                  <th>Datum</th>
                  <th>Notiz</th>
                  <th>Quelle</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map(d => editingId === d.id ? (
                  <tr key={d.id}>
                    <td>{d.matched_at ? <span className="badge badge-green">Angekommen</span> : <span className="badge badge-grey">Offen</span>}</td>
                    <td><input className="form-control form-control--sm" value={editForm.reference} onChange={e => setEditForm(f => ({ ...f, reference: e.target.value }))} /></td>
                    <td><input className="form-control form-control--sm" value={editForm.company} onChange={e => setEditForm(f => ({ ...f, company: e.target.value }))} /></td>
                    <td><input className="form-control form-control--sm" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></td>
                    <td><input className="form-control form-control--sm" type="date" value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} /></td>
                    <td><input className="form-control form-control--sm" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} /></td>
                    <td className="dv-muted">{d.source === 'manual' ? 'Manuell' : 'Import'}</td>
                    <td className="dv-actions">
                      <button className="btn btn-primary btn-sm" onClick={() => saveEdit(d.id)}>Speichern</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>Abbrechen</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={d.id} className={d.matched_at ? 'dv-row--arrived' : ''}>
                    <td>
                      {d.matched_at ? (
                        <span className="badge badge-green" title={arrivalTitle(d)}>Angekommen {fmtTime(d.matched_at)}</span>
                      ) : (
                        <span className="badge badge-grey">Offen</span>
                      )}
                    </td>
                    <td className="dv-reference">{d.reference}</td>
                    <td>{d.company || '—'}</td>
                    <td>{d.name || '—'}</td>
                    <td className="dv-muted">{fmtDay(d.delivery_date)}</td>
                    <td className="dv-muted dv-notes" title={d.notes}>{d.notes || '—'}</td>
                    <td className="dv-muted">{d.source === 'manual' ? 'Manuell' : 'Import'}</td>
                    <td className="dv-actions">
                      {d.matched_at ? (
                        <button className="btn btn-ghost btn-sm" onClick={() => setArrived(d.id, false)}>Zurücksetzen</button>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => setArrived(d.id, true)}>Als angekommen markieren</button>
                      )}
                      <button className="btn btn-ghost btn-sm" onClick={() => {
                        setEditingId(d.id)
                        setEditForm({ reference: d.reference, company: d.company, name: d.name, notes: d.notes, date: d.delivery_date })
                      }}>Bearbeiten</button>
                      <button className="btn btn-ghost btn-sm dv-btn-delete" onClick={() => deleteEntry(d.id)}>Löschen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Settings ──────────────────────────────────────────────────── */}
      {settings && (
        <div className="card">
          <div className="section-title">Einstellungen</div>

          <div className="dv-setting">
            <label className="dv-setting-label">Lieferung für einen früheren Tag angemeldet</label>
            <select className="form-control dv-setting-control" value={settings.pastDatePolicy}
              onChange={e => setSettings(s => ({ ...s, pastDatePolicy: e.target.value }))}>
              <option value="allow">Weiter lassen, solange der Eintrag in der Liste ist</option>
              <option value="block">Ablehnen</option>
            </select>
            <p className="dv-hint">Eine Lieferung für einen späteren Tag wird immer abgelehnt („zu früh").</p>
          </div>

          <div className="dv-setting">
            <label className="dv-setting-label">Einträge automatisch löschen</label>
            <div className="dv-radio-group">
              <label className="dv-radio">
                <input type="radio" checked={settings.retention.mode === 'off'} onChange={() => setRetention({ mode: 'off' })} />
                Aus
              </label>
              <label className="dv-radio">
                <input type="radio" checked={settings.retention.mode === 'end_of_day'} onChange={() => setRetention({ mode: 'end_of_day' })} />
                Am Ende des Liefertags
              </label>
              <label className="dv-radio">
                <input type="radio" checked={settings.retention.mode === 'days'} onChange={() => setRetention({ mode: 'days' })} />
                Nach
                <input type="number" min="1" max="365" className="form-control dv-days-input"
                  value={settings.retention.days} disabled={settings.retention.mode !== 'days'}
                  onChange={e => setRetention({ days: Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 1)) })} />
                Tagen
              </label>
            </div>
            {settings.retention.mode === 'end_of_day' && settings.pastDatePolicy === 'allow' && (
              <p className="dv-hint dv-hint--warn">
                Hinweis: Mit „Am Ende des Liefertags" ist ein Eintrag am nächsten Tag schon gelöscht. Ein Fahrer, der einen
                Tag zu spät kommt, wird dann als „nicht gefunden" abgewiesen.
              </p>
            )}
          </div>

          <button className="btn btn-primary btn-sm" onClick={saveSettings} disabled={settingsSaving || !settingsDirty}>
            {settingsSaving ? <><span className="spinner spinner-sm" /> Speichern…</> : 'Einstellungen speichern'}
          </button>
        </div>
      )}

      {/* ── Import preview dialog ─────────────────────────────────────── */}
      {pendingImport && (
        <div className="dv-modal-overlay" onClick={() => !importing && setPendingImport(null)}>
          <div className="dv-modal" onClick={e => e.stopPropagation()}>
            <h2 className="dv-modal-title">Import prüfen</h2>
            <p className="dv-muted">{pendingImport.fileName} · {pendingImport.rows.length} Zeilen</p>

            {pendingImport.preview.summary.length === 0 ? (
              <div className="alert alert-error">Die Datei enthält keine Zeile, die übernommen werden kann.</div>
            ) : (
              <table className="dv-table dv-preview-table">
                <thead>
                  <tr><th>Tag</th><th>Neu</th><th>Geändert</th><th>Entfernt</th><th>Bleibt</th></tr>
                </thead>
                <tbody>
                  {pendingImport.preview.summary.map(s => (
                    <tr key={s.date}>
                      <td className="dv-reference">{fmtDay(s.date)}</td>
                      <td>+{s.inserted}</td>
                      <td>{s.updated}</td>
                      <td className={s.deleted ? 'dv-text-warn' : ''}>
                        {s.deleted}{s.deletedManual ? ` (davon ${s.deletedManual} manuell angelegt)` : ''}
                      </td>
                      <td className="dv-muted">
                        {[s.keptArrived && `${s.keptArrived} angekommen`, s.keptManual && `${s.keptManual} manuell`].filter(Boolean).join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <label className="dv-checkbox">
              <input type="checkbox" checked={pendingImport.keepManual} disabled={importing}
                onChange={e => toggleKeepManual(e.target.checked)} />
              Manuell angelegte Einträge behalten
            </label>

            {pendingImport.preview.rejected.length > 0 && (
              <div className="dv-rejected">
                <div className="dv-rejected-title">{pendingImport.preview.rejected.length} Zeile(n) werden übersprungen:</div>
                <ul>
                  {pendingImport.preview.rejected.slice(0, 50).map(r => (
                    <li key={r.line}>Zeile {r.line}{r.reference ? ` (${r.reference})` : ''}: {r.reason}</li>
                  ))}
                  {pendingImport.preview.rejected.length > 50 && <li>… und {pendingImport.preview.rejected.length - 50} weitere</li>}
                </ul>
              </div>
            )}

            <div className="dv-modal-actions">
              <button className="btn btn-ghost" onClick={() => setPendingImport(null)} disabled={importing}>Abbrechen</button>
              <button className="btn btn-primary" onClick={applyImport}
                disabled={importing || pendingImport.preview.summary.length === 0}>
                {importing ? <><span className="spinner spinner-sm" /> Importiere…</> : 'Import übernehmen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** Tooltip on the "Angekommen" badge: who, and how it was marked. */
function arrivalTitle(d) {
  if (d.matched_by && d.matched_by !== 'kiosk') return `Manuell markiert von ${d.matched_by}`
  const who = [d.checkin_first_name, d.checkin_last_name].filter(Boolean).join(' ')
  return who ? `Check-in am Terminal: ${who}` : 'Check-in am Terminal'
}

export default Lieferavis
